/**
 * Searching a project's vector index — UX-05.
 *
 * The fourth and last surface for vector search: an MCP tool, a REST route,
 * a CLI command and an SDK client already existed. Somebody debugging "why
 * doesn't my search find anything" was the one person with nowhere to look.
 *
 * A search box and nothing else, because a vector index cannot be listed -
 * there is no "show me everything" to ask it. The page says so rather than
 * leaving somebody hunting for the browse view.
 *
 * Every judgement is in lib/vectors.ts, where tests hold it.
 */

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useApi } from '../lib/useApi.js'
import { ApiError } from '../lib/api.js'
import { Empty, ErrorBox, Loading, PageHeader } from '../components/ui.js'
import {
  EMPTY_NEXT_STEPS, NO_BROWSE_NOTE, SCORE_NOTE,
  emptyMessage, formatScore, searchState, summariseMetadata,
  type VectorMatch,
} from '../lib/vectors.js'

export default function Vectors() {
  const { id = '' } = useParams()
  const api = useApi()

  const [query, setQuery] = useState('')
  /** What the results on screen were actually fetched for. */
  const [searched, setSearched] = useState('')
  const [matches, setMatches] = useState<VectorMatch[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)

  async function search(e: React.FormEvent) {
    e.preventDefault()
    const text = query.trim()
    if (!text || loading) return
    setLoading(true)
    setError(null)
    try {
      const result = await api.searchVectors(id, { text, topK: 20 })
      setMatches(result.matches ?? [])
      setSearched(text)
    } catch (err) {
      // A project provisioned before vectors existed has no index, and the
      // edge says so. That is not a failed search, it is a different page.
      if (err instanceof ApiError && (err.status === 501 || err.status === 503)) {
        setUnavailable(true)
      } else {
        setError(err instanceof ApiError ? err.message : 'That search could not be run.')
      }
    } finally {
      setLoading(false)
    }
  }

  const state = searchState({ searched, matches })

  return (
    <>
      <PageHeader title="Vector search" />
      <p className="sub">
        Search by meaning rather than by words. {NO_BROWSE_NOTE}
      </p>

      {unavailable ? (
        <Empty>
          This project has no vector index. It was created before vectors existed, so there is
          nothing to search — new projects get one automatically.
        </Empty>
      ) : (
        <>
          <form style={{ margin: '16px 0' }} onSubmit={search}>
            <label htmlFor="vector-query" className="sub" style={{ display: 'block', marginBottom: 6 }}>
              Search text
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                id="vector-query"
                className="input"
                placeholder="What would somebody actually ask?"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ flex: 1 }}
              />
              <button className="btn btn-primary" type="submit" disabled={loading || query.trim() === ''}>
                {loading ? 'Searching…' : 'Search'}
              </button>
            </div>
          </form>

          {error && <ErrorBox>{error}</ErrorBox>}

          {loading && matches.length === 0 ? <Loading /> : state.kind === 'idle' ? (
            <Empty>Search the index to see what is in it.</Empty>
          ) : state.kind === 'empty' ? (
            <>
              <Empty>{emptyMessage(state)}</Empty>
              <ul className="sub" style={{ margin: '12px 0 0 18px' }}>
                {EMPTY_NEXT_STEPS.map((line) => <li key={line}>{line}</li>)}
              </ul>
            </>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr><th>score</th><th>id</th><th>metadata</th></tr>
                </thead>
                <tbody>
                  {state.matches.map((m) => (
                    <tr key={m.id}>
                      <td className="mono">{formatScore(m.score)}</td>
                      <td className="mono">{m.id}</td>
                      <td className="sub small">{summariseMetadata(m.metadata)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Next to the numbers, not in a footnote: 0.81 reads as 81%. */}
              <p className="sub small" style={{ marginTop: 8 }}>{SCORE_NOTE}</p>
            </>
          )}
        </>
      )}
    </>
  )
}
